/**
 * Message types for communication between Office Dialog and Task Pane
 * during OAuth authentication flows.
 *
 * `auth:*` messages belong to the Clerk login surface; `msal:token` carries the
 * Microsoft identity token back from the Connect Mailbox dialog. Errors and
 * cancellations from either surface ride the shared `auth:error`/`auth:cancel`
 * shapes — each surface listens on its own dialog instance, so there is no
 * cross-talk.
 */
export type DialogToParentMessage =
  | { type: 'auth:success'; userId: string }
  | { token: string; type: 'auth:bootstrap' }
  | { accessToken: string; email: string | null; type: 'msal:token' }
  | { error: string; type: 'auth:error' }
  | { type: 'auth:cancel' };

/**
 * Type guard to check if a message is a valid DialogToParentMessage
 * @param {unknown} message - The message to check
 * @returns {boolean} - True if the message is a valid DialogToParentMessage
 */
export function isDialogToParentMessage(message: unknown): message is DialogToParentMessage {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;
  return (
    (msg['type'] === 'auth:success' && typeof msg['userId'] === 'string') ||
    (msg['type'] === 'auth:bootstrap' && typeof msg['token'] === 'string') ||
    (msg['type'] === 'msal:token' &&
      typeof msg['accessToken'] === 'string' &&
      (msg['email'] === null || typeof msg['email'] === 'string')) ||
    (msg['type'] === 'auth:error' && typeof msg['error'] === 'string') ||
    msg['type'] === 'auth:cancel'
  );
}
